// An external store, not component state: quotes arrive several times a second
// and every row reads them.
import {
  type ServerFrame,
  SOCKET_CLOSED_SIGNED_OUT,
  SOCKET_PATH,
  serverFrameSchema,
  type TelegramState,
  type Viewer,
  type WireAnalystRef,
  type WireBook,
  type WireKindTally,
  type WireNotification,
  type WirePosition,
  type WireQuote,
  type WireRecommendation,
  type WireRoiRecommendation,
} from '@alphadesk/web/contract.ts';
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { syncClock } from './clock.ts';

export type ConnectionState = 'connecting' | 'live' | 'offline';

export interface StreamState {
  connection: ConnectionState;
  viewer: Viewer | null;
  books: WireBook[];
  analysts: WireAnalystRef[];
  recommendations: WireRecommendation[];
  positions: WirePosition[];
  record: WireRoiRecommendation[];
  quotes: ReadonlyMap<number, WireQuote>;
  notifications: WireNotification[];
  unread: number;
  tally: WireKindTally[];
  /** When each note marked read since load was, so a page fetched apart from the socket's can stamp it. */
  receipts: ReadonlyMap<number, string>;
  /** Only news since load; the state of record is read from `/api/telegram`. */
  telegram: TelegramState | null;
  revision: number;
}

const EMPTY: StreamState = {
  connection: 'connecting',
  viewer: null,
  books: [],
  analysts: [],
  recommendations: [],
  positions: [],
  record: [],
  quotes: new Map(),
  notifications: [],
  unread: 0,
  tally: [],
  receipts: new Map(),
  telegram: null,
  revision: 0,
};

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 15_000;

function upsertRows<T extends { id: number }>(
  rows: readonly T[],
  upsert: readonly T[],
  remove: readonly number[],
): T[] {
  const byId = new Map(rows.map((row) => [row.id, row]));
  for (const id of remove) byId.delete(id);
  for (const row of upsert) byId.set(row.id, row);
  return [...byId.values()];
}

const newestFirst = (rows: readonly WireNotification[]): WireNotification[] =>
  [...rows].sort((a, b) => b.id - a.id);

class StreamStore {
  private state: StreamState = EMPTY;
  private listeners = new Set<() => void>();
  private socket: WebSocket | null = null;
  private retries = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  snapshot = (): StreamState => this.state;

  private set(patch: Partial<StreamState>): void {
    this.state = { ...this.state, ...patch, revision: this.state.revision + 1 };
    for (const listener of this.listeners) listener();
  }

  start(): void {
    this.stopped = false;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.socket?.close(1000, 'leaving');
    this.socket = null;
  }

  private open(): void {
    if (this.stopped) return;
    const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(`${scheme}//${window.location.host}${SOCKET_PATH}`);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.retries = 0;
      this.set({ connection: 'live' });
    });

    socket.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;
      let raw: unknown;
      try {
        raw = JSON.parse(event.data);
      } catch {
        return;
      }
      const parsed = serverFrameSchema.safeParse(raw);
      if (parsed.success) this.apply(parsed.data);
    });

    socket.addEventListener('close', (event) => {
      if (this.socket === socket) this.socket = null;
      if (this.stopped) return;
      // The session ended; reconnecting would be refused, so let the gate explain.
      if (event.code === SOCKET_CLOSED_SIGNED_OUT) {
        this.stop();
        window.location.assign('/');
        return;
      }
      this.set({ connection: 'offline' });
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.retries, RECONNECT_MAX_MS);
      this.retries += 1;
      this.timer = setTimeout(() => this.open(), delay);
    });

    socket.addEventListener('error', () => socket.close());
  }

  private apply(frame: ServerFrame): void {
    switch (frame.t) {
      case 'hello':
        syncClock(frame.now);
        this.set({ viewer: frame.viewer, books: frame.books });
        return;
      case 'viewer':
        this.set({ viewer: frame.viewer });
        return;
      case 'snapshot':
        this.set({
          recommendations: frame.recommendations,
          positions: frame.positions,
          books: frame.books,
          analysts: frame.analysts,
          record: frame.record,
          quotes: new Map(frame.quotes.map((quote) => [quote.token, quote])),
          notifications: frame.notifications,
          unread: frame.unread,
          tally: frame.tally,
        });
        return;
      case 'notifications': {
        const { read } = frame;
        let rows = upsertRows(this.state.notifications, frame.upsert, []);
        let receipts = this.state.receipts;
        if (read) {
          const marked = new Set(read.ids);
          rows = rows.map((row) =>
            marked.has(row.id) && row.readAt === null ? { ...row, readAt: read.at } : row,
          );
          receipts = new Map([...receipts, ...read.ids.map((id) => [id, read.at] as const)]);
        }
        this.set({
          notifications: newestFirst(rows),
          receipts,
          unread: frame.unread,
          tally: frame.tally,
        });
        return;
      }
      case 'telegram':
        this.set({ telegram: frame.telegram });
        return;
      case 'recommendations':
        this.set({
          recommendations: upsertRows(this.state.recommendations, frame.upsert, frame.remove),
          record: frame.record,
        });
        return;
      case 'positions':
        this.set({ positions: upsertRows(this.state.positions, frame.upsert, frame.remove) });
        return;
      case 'quotes': {
        const quotes = new Map(this.state.quotes);
        for (const quote of frame.quotes) quotes.set(quote.token, quote);
        this.set({ quotes });
        return;
      }
    }
  }

  resync(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ t: 'resync' }));
    }
  }

  extendInbox(older: readonly WireNotification[]): void {
    if (older.length === 0) return;
    this.set({ notifications: newestFirst(upsertRows(this.state.notifications, older, [])) });
  }
}

export function useStream(): StreamState & {
  resync: () => void;
  extendInbox: (older: readonly WireNotification[]) => void;
} {
  const store = useMemo(() => new StreamStore(), []);
  const ref = useRef(store);
  ref.current = store;

  useEffect(() => {
    store.start();
    return () => store.stop();
  }, [store]);

  // A hidden tab's socket may look unbroken yet have missed frames.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') store.resync();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [store]);

  const state = useSyncExternalStore(store.subscribe, store.snapshot, store.snapshot);
  const resync = useCallback(() => ref.current.resync(), []);
  const extendInbox = useCallback(
    (older: readonly WireNotification[]) => ref.current.extendInbox(older),
    [],
  );
  return { ...state, resync, extendInbox };
}
